import React, { useEffect, useMemo, useState } from "react";
import Head from "../../../../layout/head/Head";
import Content from "../../../../layout/content/Content";
import TablePageHeading from "../page_heading/TablePageHeading";
import { Col, Row, Button } from "../../../../components/Component";
import { Spinner } from "reactstrap";
import { motion } from "framer-motion";
import {
  BiReceipt,
  BiCheckCircle,
  BiErrorCircle,
  BiRefresh,
  BiDownload,
  BiUndo,
  BiQr,
  BiFile,
} from "react-icons/bi";
import { useNavigate, useParams } from "react-router-dom";
import {
  Document as PdfDocument,
  Page as PdfPage,
  Text as PdfText,
  View as PdfView,
  StyleSheet as PdfStyleSheet,
  pdf,
} from "@react-pdf/renderer";
import TextInput from "../../../components/inputs/TextInput";
import * as app_consts from "../../../../constants/react_app_consts";
import { messageToast } from "../../../../utils/Utils";
import {
  getSmartInvoiceById,
  refundSmartInvoice,
  retrySmartInvoiceSubmission,
} from "../../../../services/smart_invoice_service";

const type = "SMART_INVOICE_DETAILS";

const toNum = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatMoney = (value) =>
  toNum(value).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const normalizeStatus = (status = "") => String(status).trim().toLowerCase();

const getStatusBadgeClass = (status = "") => {
  const normalized = normalizeStatus(status);

  if (normalized === "validated") return "badge badge-dim bg-success";
  if (normalized === "pending" || normalized === "pending_retry") return "badge badge-dim bg-warning";
  if (normalized === "failed") return "badge badge-dim bg-danger";
  if (normalized === "draft") return "badge badge-dim bg-secondary";
  if (normalized === "refunded") return "badge badge-dim bg-info";

  return "badge badge-dim bg-secondary";
};

const invoicePdfStyles = PdfStyleSheet.create({
  page: { padding: 28, fontSize: 10, color: "#111827", backgroundColor: "#ffffff" },
  header: { marginBottom: 18 },
  title: { fontSize: 20, fontWeight: 700, marginBottom: 4 },
  subtitle: { fontSize: 11, color: "#475569" },
  section: { marginBottom: 14, borderWidth: 1, borderColor: "#dbe1ea" },
  row: { flexDirection: "row" },
  cell: { flex: 1, padding: 8, borderRightWidth: 1, borderBottomWidth: 1, borderColor: "#dbe1ea" },
  lastCell: { borderRightWidth: 0 },
  label: { fontSize: 8, fontWeight: 700, color: "#64748b", marginBottom: 4, textTransform: "uppercase" },
  value: { fontSize: 10, fontWeight: 600 },
  lineTitle: { fontSize: 12, fontWeight: 700, marginBottom: 8 },
});

const PdfCell = ({ label, value, last = false }) => (
  <PdfView style={[invoicePdfStyles.cell, last ? invoicePdfStyles.lastCell : null]}>
    <PdfText style={invoicePdfStyles.label}>{label}</PdfText>
    <PdfText style={invoicePdfStyles.value}>{value || "--"}</PdfText>
  </PdfView>
);

const SmartInvoicePdfDocument = ({ invoice }) => (
  <PdfDocument title={invoice?.invoice_number || "Smart Invoice"}>
    <PdfPage size="A4" style={invoicePdfStyles.page}>
      <PdfView style={invoicePdfStyles.header}>
        <PdfText style={invoicePdfStyles.title}>Smart Invoice</PdfText>
        <PdfText style={invoicePdfStyles.subtitle}>{invoice?.invoice_number || "--"}</PdfText>
      </PdfView>

      <PdfView style={invoicePdfStyles.section}>
        <PdfView style={invoicePdfStyles.row}>
          <PdfCell label="Invoice Number" value={invoice?.invoice_number} />
          <PdfCell label="Invoice Date" value={invoice?.invoice_date} />
          <PdfCell label="Status" value={invoice?.status} last />
        </PdfView>
        <PdfView style={invoicePdfStyles.row}>
          <PdfCell label="Customer" value={invoice?.customer_name} />
          <PdfCell label="TPIN" value={invoice?.customer_tpin} />
          <PdfCell label="Delivery Note Ref" value={invoice?.delivery_note_ref} last />
        </PdfView>
      </PdfView>

      <PdfView style={invoicePdfStyles.section}>
        <PdfView style={{ padding: 10 }}>
          <PdfText style={invoicePdfStyles.lineTitle}>Invoice Lines</PdfText>
          {invoice?.invoice_lines?.map((line, index) => (
            <PdfView key={line.id || index} style={{ marginBottom: 8 }}>
              <PdfText>{`${index + 1}. ${line?.commodity || "--"} | ${line?.description || "--"}`}</PdfText>
              <PdfText>{`Qty: ${line?.tonnage || 0} MT | Price: ${invoice?.currency || "ZMW"} ${formatMoney(line?.price_per_mt)} | Total: ${invoice?.currency || "ZMW"} ${formatMoney(line?.line_total_foreign ?? line?.line_total)}`}</PdfText>
            </PdfView>
          ))}
        </PdfView>
      </PdfView>

      <PdfView style={invoicePdfStyles.section}>
        <PdfView style={invoicePdfStyles.row}>
          <PdfCell label="Subtotal" value={`ZMW ${formatMoney(invoice?.subtotal)}`} />
          <PdfCell label="VAT" value={`ZMW ${formatMoney(invoice?.vat_amount)}`} />
          <PdfCell label="Total Due" value={`ZMW ${formatMoney(invoice?.total_due)}`} last />
        </PdfView>
      </PdfView>
    </PdfPage>
  </PdfDocument>
);

const SmartInvoiceDetails = () => {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [invoice, setInvoice] = useState(null);

  const cardMotion = useMemo(
    () => ({
      hidden: { opacity: 0, y: 12, scale: 0.985 },
      show: (i = 0) => ({
        opacity: 1,
        y: 0,
        scale: 1,
        transition: { duration: 0.35, delay: i * 0.08, ease: "easeOut" },
      }),
    }),
    []
  );

  const hoverMotion = useMemo(
    () => ({
      whileHover: { y: -4, transition: { duration: 0.18, ease: "easeOut" } },
      whileTap: { scale: 0.99 },
    }),
    []
  );

  const retrieveSmartInvoiceDetails = async () => {
    setLoading(true);
    setError("");

    try {
      const foundInvoice = await getSmartInvoiceById(invoiceId);

      if (!foundInvoice) {
        setError("Smart Invoice not found.");
        setInvoice(null);
        return;
      }

      setInvoice(foundInvoice);
    } catch (err) {
      setError("Failed to retrieve Smart Invoice details.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    retrieveSmartInvoiceDetails();
  }, [invoiceId]);

  const totalGrossProfit = useMemo(
    () => (invoice?.invoice_lines || []).reduce((sum, line) => sum + toNum(line?.gross_profit), 0),
    [invoice]
  );

  const handleDownloadPDF = async () => {
    if (!invoice) return;

    try {
      const blob = await pdf(<SmartInvoicePdfDocument invoice={invoice} />).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${invoice.invoice_number || "smart-invoice"}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      messageToast("PDF download started.", app_consts.SUCCESS);
    } catch (err) {
      messageToast("Failed to download PDF.", app_consts.FAIL);
    }
  };

  const handleRefundInvoice = async () => {
    if (!invoice) return;

    try {
      await refundSmartInvoice(invoice.id);
      await retrieveSmartInvoiceDetails();
      messageToast("Refund request submitted successfully.", app_consts.SUCCESS);
    } catch (err) {
      messageToast("Failed to submit refund request.", app_consts.FAIL);
    }
  };

  const handleRetrySubmission = async () => {
    if (!invoice) return;

    try {
      await retrySmartInvoiceSubmission(invoice.id);
      await retrieveSmartInvoiceDetails();
      messageToast("Retry request submitted successfully.", app_consts.SUCCESS);
    } catch (err) {
      messageToast("Failed to retry Smart Invoice submission.", app_consts.FAIL);
    }
  };

  const IconCard = ({ icon: IconComp, title, amount }) => (
    <div className="card card-bordered h-100">
      <div className="card-inner">
        <div className="d-flex align-items-center justify-content-between mb-1">
          <div className="text-soft">{title}</div>
          <IconComp className="fs-4 text-primary" />
        </div>
        <div className="fs-22px fw-bold">{amount}</div>
      </div>
    </div>
  );

  const canDownloadPDF = Boolean(invoice);
  const canRefund = normalizeStatus(invoice?.status) === "validated";
  const canRetry = ["failed", "pending_retry"].includes(normalizeStatus(invoice?.status));

  return (
    <>
      <Head title="Smart Invoice Details" />
      <Content>
        <TablePageHeading title="Smart Invoice Details" page_type={type} pageData={invoice ? [invoice] : []} />

        {loading ? (
          <div className="card card-bordered">
            <div className="card-inner py-5">
              <div className="d-flex flex-column align-center justify-center">
                <Spinner />
                <div className="fs-15px mt-2 text-soft">Loading Smart Invoice details...</div>
              </div>
            </div>
          </div>
        ) : error ? (
          <div className="card card-bordered">
            <div className="card-inner py-5 text-center">
              <BiErrorCircle className="text-danger fs-3" />
              <div className="mt-2 fw-bold text-soft">{error}</div>
              <div className="d-flex justify-content-center gap-2 mt-3">
                <button className="btn btn-primary" onClick={retrieveSmartInvoiceDetails} type="button">Try Again</button>
                <button className="btn btn-light" onClick={() => navigate("/dashboard/smart_invoices")} type="button">Back to List</button>
              </div>
            </div>
          </div>
        ) : (
          <Row className="g-gs">
            <Col xs="12">
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.25 }}>
                <Row className="g-gs">
                  <Col xxl="3" sm="6">
                    <motion.div className="h-100" variants={cardMotion} initial="hidden" animate="show" custom={0} {...hoverMotion}>
                      <IconCard icon={BiReceipt} title="Subtotal" amount={`ZMW ${formatMoney(invoice?.subtotal)}`} />
                    </motion.div>
                  </Col>
                  <Col xxl="3" sm="6">
                    <motion.div className="h-100" variants={cardMotion} initial="hidden" animate="show" custom={1} {...hoverMotion}>
                      <IconCard icon={BiFile} title="VAT" amount={`ZMW ${formatMoney(invoice?.vat_amount)}`} />
                    </motion.div>
                  </Col>
                  <Col xxl="3" sm="6">
                    <motion.div className="h-100" variants={cardMotion} initial="hidden" animate="show" custom={2} {...hoverMotion}>
                      <IconCard icon={BiCheckCircle} title="Total Due" amount={`ZMW ${formatMoney(invoice?.total_due)}`} />
                    </motion.div>
                  </Col>
                  <Col xxl="3" sm="6">
                    <motion.div className="h-100" variants={cardMotion} initial="hidden" animate="show" custom={3} {...hoverMotion}>
                      <IconCard icon={BiReceipt} title="Gross Profit" amount={`ZMW ${formatMoney(totalGrossProfit)}`} />
                    </motion.div>
                  </Col>
                </Row>
              </motion.div>
            </Col>

            <Col xxl="8">
              <div className="card card-bordered">
                <div className="card-inner border-bottom">
                  <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <div>
                      <div className="fs-15px fw-bold">Customer-Facing Invoice Section</div>
                      <div className="text-soft small">This is the section that will appear on the customer PDF.</div>
                    </div>
                    <div>
                      <span className={getStatusBadgeClass(invoice?.status)}>{invoice?.status}</span>
                    </div>
                  </div>
                </div>

                <div className="card-inner">
                  <Row className="g-3">
                    <Col md="6"><TextInput label="Invoice Number" value={invoice?.invoice_number || ""} disabled /></Col>
                    <Col md="6"><TextInput label="Invoice Date" value={invoice?.invoice_date || ""} disabled /></Col>
                    <Col md="6"><TextInput label="Customer Name" value={invoice?.customer_name || ""} disabled /></Col>
                    <Col md="6"><TextInput label="Customer TPIN" value={invoice?.customer_tpin || ""} disabled /></Col>
                    <Col md="12"><TextInput label="Customer Address" value={invoice?.customer_address || ""} disabled /></Col>
                    <Col md="4"><TextInput label="Delivery Note Ref" value={invoice?.delivery_note_ref || ""} disabled /></Col>
                    <Col md="4"><TextInput label="Currency" value={invoice?.currency || ""} disabled /></Col>
                    <Col md="4"><TextInput label="Exchange Rate" value={invoice?.exchange_rate || ""} disabled /></Col>
                    <Col md="6"><TextInput label="RE / Reference" value={invoice?.re_reference || ""} disabled /></Col>
                    <Col md="6"><TextInput label="Smart Reference" value={invoice?.smart_reference || ""} disabled /></Col>
                  </Row>
                </div>
              </div>

              <div className="card card-bordered mt-4">
                <div className="card-inner border-bottom">
                  <div className="fs-15px fw-bold">Invoice Line Items</div>
                  <div className="text-soft small">Customer-facing invoice item breakdown.</div>
                </div>

                <div className="card-inner">
                  {(invoice?.invoice_lines || []).map((line, index) => (
                    <div key={line.id || index} className="border rounded p-3 mb-3">
                      <div className="fw-bold mb-3">Line Item #{index + 1}</div>

                      <Row className="g-3">
                        <Col md="3"><TextInput label="Commodity" value={line?.commodity || ""} disabled /></Col>
                        <Col md="3"><TextInput label="Description" value={line?.description || ""} disabled /></Col>
                        <Col md="2"><TextInput label="Tonnage (MT)" value={line?.tonnage || ""} disabled /></Col>
                        <Col md="2"><TextInput label={`Price / MT (${invoice?.currency || "ZMW"})`} value={formatMoney(line?.price_per_mt)} disabled /></Col>
                        <Col md="2"><TextInput label="Line Total" value={formatMoney(line?.line_total_foreign ?? line?.line_total)} disabled /></Col>
                      </Row>
                    </div>
                  ))}
                </div>
              </div>

              <div className="card card-bordered mt-4">
                <div className="card-inner border-bottom">
                  <div className="fs-15px fw-bold">Payment & Additional Information</div>
                </div>

                <div className="card-inner">
                  <Row className="g-3">
                    <Col md="12"><TextInput label="Payment Terms" value={invoice?.payment_terms || ""} disabled /></Col>
                    <Col md="12"><TextInput label="Bank Details" value={invoice?.bank_details || ""} disabled /></Col>
                    <Col md="12"><TextInput label="Notes" value={invoice?.notes || ""} disabled /></Col>
                  </Row>
                </div>
              </div>
            </Col>

            <Col xxl="4">
              <div className="card card-bordered">
                <div className="card-inner border-bottom">
                  <div className="fs-15px fw-bold">Smart Status</div>
                  <div className="text-soft small">ZRA validation and output summary</div>
                </div>

                <div className="card-inner">
                  <div className="d-flex justify-content-between py-2">
                    <span className="text-soft">Status</span>
                    <span className={getStatusBadgeClass(invoice?.status)}>{invoice?.status}</span>
                  </div>
                  <div className="d-flex justify-content-between py-2">
                    <span className="text-soft">Smart Reference</span>
                    <span className="fw-bold">{invoice?.smart_reference || "-"}</span>
                  </div>
                  <div className="d-flex justify-content-between py-2">
                    <span className="text-soft">Issued At</span>
                    <span className="fw-bold">{invoice?.issued_at || "-"}</span>
                  </div>

                  <hr />

                  <div className="text-soft small mb-2">QR Code</div>

                  <div className="border rounded p-4 text-center">
                    {invoice?.qr_code ? (
                      <img src={invoice.qr_code} alt="ZRA QR Code" style={{ maxWidth: "140px" }} />
                    ) : (
                      <>
                        <BiQr className="fs-1 text-soft" />
                        <div className="text-soft small mt-2">QR code will appear here after successful validation.</div>
                      </>
                    )}
                  </div>
                </div>
              </div>

              <div className="card card-bordered mt-4">
                <div className="card-inner border-bottom">
                  <div className="fs-15px fw-bold">Internal Company Section</div>
                  <div className="text-soft small">Internal costing and profitability. Not included in PDF.</div>
                </div>

                <div className="card-inner">
                  {(invoice?.invoice_lines || []).map((line, index) => (
                    <div key={`internal-${line.id || index}`} className="border rounded p-3 mb-3">
                      <div className="fw-bold mb-3">Internal Costing #{index + 1}</div>

                      <div className="d-flex justify-content-between py-1">
                        <span className="text-soft">Cost / MT</span>
                        <span className="fw-bold">ZMW {formatMoney(line?.cost_per_mt)}</span>
                      </div>
                      <div className="d-flex justify-content-between py-1">
                        <span className="text-soft">Transport Ex SA</span>
                        <span className="fw-bold">ZMW {formatMoney(line?.transport_ex_sa)}</span>
                      </div>
                      <div className="d-flex justify-content-between py-1">
                        <span className="text-soft">Transport Local</span>
                        <span className="fw-bold">ZMW {formatMoney(line?.transport_local)}</span>
                      </div>
                      <div className="d-flex justify-content-between py-1">
                        <span className="text-soft">Import Costs</span>
                        <span className="fw-bold">ZMW {formatMoney(line?.import_costs)}</span>
                      </div>
                      <div className="d-flex justify-content-between py-1">
                        <span className="text-soft">Commission</span>
                        <span className="fw-bold">ZMW {formatMoney(line?.commission)}</span>
                      </div>

                      <hr />

                      <div className="d-flex justify-content-between py-1">
                        <span className="text-soft">Total Cost / MT</span>
                        <span className="fw-bold">ZMW {formatMoney(line?.total_cost_per_mt)}</span>
                      </div>
                      <div className="d-flex justify-content-between py-1">
                        <span className="text-soft">Total Cost</span>
                        <span className="fw-bold">ZMW {formatMoney(line?.total_cost)}</span>
                      </div>
                      <div className="d-flex justify-content-between py-1">
                        <span className="text-soft">Gross Profit</span>
                        <span className="fw-bold">ZMW {formatMoney(line?.gross_profit)}</span>
                      </div>
                      <div className="d-flex justify-content-between py-1">
                        <span className="text-soft">Margin %</span>
                        <span className="fw-bold">{formatMoney(line?.margin_pct)}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="card card-bordered mt-4">
                <div className="card-inner border-bottom">
                  <div className="fs-15px fw-bold">Totals Summary</div>
                </div>

                <div className="card-inner">
                  <div className="d-flex justify-content-between py-2">
                    <span className="text-soft">Subtotal</span>
                    <span className="fw-bold">ZMW {formatMoney(invoice?.subtotal)}</span>
                  </div>
                  <div className="d-flex justify-content-between py-2">
                    <span className="text-soft">VAT</span>
                    <span className="fw-bold">ZMW {formatMoney(invoice?.vat_amount)}</span>
                  </div>
                  <div className="d-flex justify-content-between py-2">
                    <span className="text-soft">Total Due</span>
                    <span className="fw-bold">ZMW {formatMoney(invoice?.total_due)}</span>
                  </div>
                  <div className="d-flex justify-content-between py-2">
                    <span className="text-soft">Gross Profit</span>
                    <span className="fw-bold">ZMW {formatMoney(totalGrossProfit)}</span>
                  </div>
                </div>
              </div>

              <div className="card card-bordered mt-4">
                <div className="card-inner">
                  <div className="d-grid gap-2">
                    <Button color="primary" onClick={handleDownloadPDF} disabled={!canDownloadPDF}>
                      <BiDownload className="me-1" />
                      Download PDF
                    </Button>

                    <Button color="warning" onClick={handleRetrySubmission} disabled={!canRetry}>
                      <BiRefresh className="me-1" />
                      Retry Submission
                    </Button>

                    <Button color="danger" onClick={handleRefundInvoice} disabled={!canRefund}>
                      <BiUndo className="me-1" />
                      Refund Invoice
                    </Button>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        )}
      </Content>
    </>
  );
};

export default SmartInvoiceDetails;
